import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";

/**
 * Reusable shared-element page transition ("section image expands into page").
 *
 * How it works:
 *  1. On click we capture ONLY the section's image element — its exact rect,
 *     src and natural aspect ratio. The card's text/gradient/caption are never
 *     touched, so nothing gets stretched as the box grows.
 *  2. A theme-aware curtain (bg-background) fades in to mask the route swap,
 *     then the image expands from its on-screen rect to a capped, aspect-correct
 *     box that is centered horizontally and pinned to the top of the page.
 *  3. Once the destination page has actually mounted (RouteReadyProbe) — the
 *     lazy chunk may still be loading — the image quickly fades out (no extra
 *     zoom), revealing the new page which is always scrolled to the top.
 *
 * Dark/light support comes for free: every layer uses the `background` /
 * design-token colors, so the curtain composites perfectly in both themes
 * and there is no flash of the wrong colour.
 *
 * Creative control: tune PAGE_TRANSITION_TIMING below (durations, easing,
 * hold behaviour, expand sizing, reveal zoom).
 */
/**
 * Destination routes whose hero is NOT an image (canvas globes, grids, etc.).
 * For these we skip the shared-image morph entirely and navigate directly so
 * the user isn't shown a blank expanding panel with nothing to fade into.
 */
export const NON_IMAGE_ROUTES = [
  "/periodic-table-miracle",
  "/geographical-map",
  "/land-sea-ratio",
  "/sacred-geometry",
];

export const PAGE_TRANSITION_TIMING = {
  /** Curtain (theme backdrop) fade-in duration, seconds. */
  curtainIn: 0.08,
  /** Delay before the route actually swaps — the curtain hides it. */
  navigateDelay: 0.05,
  /** Card image -> hero expand duration, seconds. Graceful grow so the eye can follow. */
  expand: 0.35,
  /** Max extra seconds to hold while a slow chunk loads (safety ceiling). */
  hold: 1.0,
  /** Reveal (image dissolves into the page) duration, seconds. Slow enough to feel soft, fast enough to feel instant. */
  reveal: 0.6,
  /** Smooth ease for the expand. */
  expandEase: [0.25, 0.1, 0.25, 1],
  /** Gentle ease for the reveal dissolve. */
  revealEase: [0.4, 0, 0.2, 1],
  /** No extra zoom on reveal — the image must not grow any further. */
  revealScale: 1,
  /** Expanded image width — spans full content width with page padding,
   * mirroring how hero images render on destination pages. */
  expandWidthRatio: 1.0,
  /** Hard pixel cap so the image never exceeds content width. */
  expandMaxWidth: 1080,
  /** Expanded image height as a fraction of the viewport. */
  expandHeightRatio: 0.55,
  /** Horizontal padding (px) mirroring the page content container. */
  contentPadding: 40,
  /** Top padding (px) so the expanded image sits below the navbar/page title. */
  topPadding: 120,
};

const Z_CURTAIN = 9998;
const Z_CARD = 9999;

const PageTransitionContext = createContext(null);

/** Low-level context hook (markRouteReady / startTransition). */
export const usePageTransition = () => useContext(PageTransitionContext);

/* ------------------------------------------------------------------ */
/* Source capture                                                       */
/* ------------------------------------------------------------------ */

/**
 * Locate the "card" element inside a home-section component whose bounds we
 * should expand from. Cascade covers every section layout in the codebase:
 *  1. `.visual-container` — the standard image card
 *  2. `.grid-container`   — Periodic Table section
 *  3. first <img> parent   — "rounded-3xl" style cards (e.g. History section)
 *  4. first <canvas> parent — 3D globe sections (gradient fallback panel)
 *  5. the section root itself as a last resort
 */
const findSourceElement = (root) => {
  if (!root || typeof root.querySelector !== "function") return null;
  return (
    root.querySelector(".visual-container") ||
    root.querySelector(".grid-container") ||
    (() => {
      const img = root.querySelector("img");
      return img ? img.closest("div") || img : null;
    })() ||
    (() => {
      const canvas = root.querySelector("canvas");
      return canvas ? canvas.parentElement : null;
    })() ||
    root
  );
};

/**
 * Capture the minimum needed to morph a hero image: the image's own rect
 * (NOT the whole card, so no text/gradient/caption gets stretched), its src
 * and natural aspect ratio. Sections without an <img> (canvas globes) fall
 * back to a flat panel filled with the card's computed background.
 */
const captureSnapshot = (element) => {
  if (!element) return null;

  const img = element.querySelector ? element.querySelector("img") : null;
  const rect = (img || element).getBoundingClientRect();
  if (rect.width < 2 || rect.height < 2) return null;

  // Radius comes from the card (it clips the image via overflow-hidden), so
  // frame 1 of the overlay matches the rounded corners the user actually sees.
  const cardStyle = window.getComputedStyle(element);
  const imgStyle = img ? window.getComputedStyle(img) : cardStyle;

  let aspect = 0;
  if (img && img.naturalWidth && img.naturalHeight) {
    aspect = img.naturalWidth / img.naturalHeight;
  }

  const radius =
    cardStyle.borderRadius && cardStyle.borderRadius !== "0px"
      ? cardStyle.borderRadius
      : imgStyle.borderRadius && imgStyle.borderRadius !== "0px"
      ? imgStyle.borderRadius
      : "0px";

  return {
    rect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
    radius,
    src: img ? img.currentSrc || img.src : null,
    aspect,
    background: imgStyle.backgroundImage !== "none" ? imgStyle.backgroundImage : imgStyle.backgroundColor,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
  };
};

/**
 * Target box for the expanded image: spans the full width of the content
 * section (with page padding on both sides), pinned below the navbar/title.
 * Uses the captured border-radius so the expanded image matches the card the
 * user clicked, preserving aspect ratio so nothing distorts.
 */
const expandTarget = (snapshot) => {
  const { viewportWidth: vw, viewportHeight: vh, aspect, radius } = snapshot;
  const { contentPadding, topPadding } = PAGE_TRANSITION_TIMING;

  // Full content width with page padding on both sides — mirrors how hero
  // images render on destination pages (text + image across the content area).
  const imageWidth = Math.min(vw - 2 * contentPadding, PAGE_TRANSITION_TIMING.expandMaxWidth);
  const imageHeight = aspect ? imageWidth / aspect : vh * PAGE_TRANSITION_TIMING.expandHeightRatio;

  return {
    top: topPadding,
    left: (vw - imageWidth) / 2, // centered horizontally on the page
    width: imageWidth,
    height: Math.min(imageHeight, vh * 0.6),
    borderRadius: radius,
  };
};


/* ------------------------------------------------------------------ */
/* Overlay                                                             */
/* ------------------------------------------------------------------ */

const TransitionOverlay = ({ snapshot, phase, onPhaseComplete, onRevealComplete }) => {
  if (!snapshot) return null;

  const { rect, radius, src, background } = snapshot;

  // Start exactly where the clicked card image sits; end centered at the top.
  const initial = {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
    borderRadius: radius,
    opacity: 1,
  };

  const target = expandTarget(snapshot);

  const animateByPhase = {
    expand: { ...target, opacity: 1 },
    // Hold: identical geometry — we simply wait here for the route to mount.
    hold: { ...target, opacity: 1 },
    // Reveal: fade the image out to uncover the (already top-scrolled) page.
    reveal: { ...target, opacity: 0, scale: PAGE_TRANSITION_TIMING.revealScale },
  };

  const transitionByPhase = {
    expand: { duration: PAGE_TRANSITION_TIMING.expand, ease: PAGE_TRANSITION_TIMING.expandEase },
    hold: { duration: PAGE_TRANSITION_TIMING.hold, ease: "linear" },
    reveal: { duration: PAGE_TRANSITION_TIMING.reveal, ease: PAGE_TRANSITION_TIMING.revealEase },
  };

  // The curtain stays opaque while the new page mounts (hiding the route swap)
  // and fades out together with the image during the reveal.
  const curtainTarget = phase === "reveal" ? { opacity: 0 } : { opacity: 1 };

  return createPortal(
    <>
      <motion.div
        key="pt-curtain"
        aria-hidden="true"
        className="fixed inset-0 bg-background"
        style={{ zIndex: Z_CURTAIN }}
        initial={{ opacity: 0 }}
        animate={curtainTarget}
        transition={{
          duration: phase === "reveal" ? PAGE_TRANSITION_TIMING.reveal : PAGE_TRANSITION_TIMING.curtainIn,
          ease: "easeOut",
        }}
      />
      <motion.div
        key="pt-card"
        aria-hidden="true"
        className="fixed overflow-hidden"
        style={{
          zIndex: Z_CARD,
          willChange: "top,left,width,height",
          boxShadow:
            "0 25px 50px -12px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.5)",
        }}
        initial={initial}
        animate={animateByPhase[phase] || animateByPhase.expand}
        transition={transitionByPhase[phase] || transitionByPhase.expand}
        onAnimationComplete={() => {
          if (phase === "reveal") {
            if (onRevealComplete) onRevealComplete();
          } else if (onPhaseComplete) {
            onPhaseComplete(phase);
          }
        }}
      >
        {src ? (
          // Only the image morphs — never the card's text/gradient/caption,
          // so nothing gets stretched while the box grows.
          <img
            src={src}
            alt=""
            className="w-full h-full object-cover"
            draggable="false"
          />
        ) : (
          <div className="w-full h-full" style={{ background: background }} />
        )}
      </motion.div>
    </>,
    document.body
  );
};

/* ------------------------------------------------------------------ */
/* Provider                                                            */
/* ------------------------------------------------------------------ */

export const PageTransitionProvider = ({ children }) => {
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();

  const [session, setSession] = useState(null);
  const [phase, setPhase] = useState("idle");

  const timersRef = useRef([]);
  const readyRef = useRef(false);
  const sessionRef = useRef(null);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((id) => clearTimeout(id));
    timersRef.current = [];
  }, []);

  const schedule = useCallback((fn, ms) => {
    const id = setTimeout(fn, ms);
    timersRef.current.push(id);
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  /** Called by RouteReadyProbe once the destination route element has mounted.
   * Kept for compatibility, but we no longer gate the reveal on page load —
   * the image fades out immediately after the expand based on timing alone,
   * so the page feels like it opens instantly.
   */
  const markRouteReady = useCallback(() => {
    readyRef.current = true;
  }, []);

  /** Called by the overlay when the animation for `phase` finishes. */
  const handlePhaseComplete = useCallback((completed) => {
    if (completed === "expand") {
      // Fade out immediately — do not wait for the destination page to load.
      setPhase("reveal");
    }
  }, []);

  const finish = useCallback(() => {
    clearTimers();
    readyRef.current = false;
    sessionRef.current = null;
    setPhase("idle");
    setSession(null);
  }, [clearTimers]);

  /**
   * Begin a transition towards `to`. `source` is the DOM node of the clicked
   * section card; if it cannot be captured we fall back to an instant
   * route change so navigation is never blocked.
   */
  const startTransition = useCallback(
    (to, source, options) => {
      if (!to) return;

      // Exception: destination pages whose hero is NOT an image (canvas
      // globes, grids, etc.). There's nothing to morph into, so skip the
      // shared-image overlay entirely and navigate directly.
      if (NON_IMAGE_ROUTES.includes(to)) {
        navigate(to, options);
        return;
      }

      const snapshot = prefersReducedMotion ? null : captureSnapshot(source);
      if (!snapshot) {
        navigate(to, options);
        return;
      }

      clearTimers();
      readyRef.current = false;
      sessionRef.current = to;
      setSession(snapshot);
      setPhase("expand");

      schedule(() => {
        navigate(to, options);
      }, PAGE_TRANSITION_TIMING.navigateDelay * 1000);

      // Safety net: never trap the user behind the overlay if something goes wrong.
      schedule(finish, (PAGE_TRANSITION_TIMING.navigateDelay + PAGE_TRANSITION_TIMING.expand + PAGE_TRANSITION_TIMING.reveal) * 1000);
    },
    [clearTimers, finish, navigate, prefersReducedMotion, schedule]
  );

  const value = useMemo(
    () => ({
      startTransition,
      markRouteReady,
      isTransitioning: phase !== "idle",
    }),
    [startTransition, markRouteReady, phase]
  );

  return (
    <PageTransitionContext.Provider value={value}>
      {children}
      {session && phase !== "idle" ? (
        <TransitionOverlay
          snapshot={session}
          phase={phase}
          onPhaseComplete={handlePhaseComplete}
          onRevealComplete={finish}
        />
      ) : null}
    </PageTransitionContext.Provider>
  );
};

/**
 * Renders its children and tells the transition system when they have mounted.
 * Wrap every route element with this (see App.js).
 */
export const RouteReadyProbe = ({ children }) => {
  const { markRouteReady } = usePageTransition();

  useEffect(() => {
    if (markRouteReady) markRouteReady();
  }, [markRouteReady]);

  return children;
};

/* ------------------------------------------------------------------ */
/* Section hook                                                        */
/* ------------------------------------------------------------------ */

/**
 * Drop-in replacement for `useNavigate()` inside a home section.
 *
 *   const scope = useRef(null);            // or useAnimate() scope
 *   const navigate = useSectionTransition(scope);
 *   ...
 *   <section ref={scope}> ... <button onClick={() => navigate("/history-of-the-quran")} />
 *
 * `navigate(to, { replace, state })` behaves exactly like react-router's
 * navigate; the expanding card is resolved from the DOM at click time, so
 * sections may lay out their visuals however they like.
 */
export const useSectionTransition = (scope) => {
  const { startTransition } = usePageTransition();

  return useCallback(
    (to, options) => {
      if (!to) return;
      if (!startTransition) return;
      const source = scope && scope.current ? findSourceElement(scope.current) : null;
      startTransition(to, source, options);
    },
    [startTransition, scope]
  );
};
